import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from '../../elements/Modal';

const PAUSE_OPTIONS = [
  {
    id: 'skip_1',
    labelSuffix: 'Option_1M_Label',
    descSuffix: 'Option_1M_Desc',
    label: 'Skip 1 delivery',
    description: 'Resume after your next skipped shipment.',
  },
  {
    id: 'skip_2',
    labelSuffix: 'Option_3M_Label',
    descSuffix: 'Option_3M_Desc',
    label: 'Skip 2 deliveries',
    description: 'Resume after the next two skipped shipments.',
  },
  {
    id: 'specific_date',
    labelSuffix: 'Option_Date_Label',
    descSuffix: 'Option_Date_Desc',
    label: 'Choose a specific date',
    description: 'Resume it on the specific date of your choice.',
  },
];

const MODE_COPY = {
  pause: {
    prefix: 'PauseModal',
    title: 'PauseModal_Title',
    subtitle: 'PauseModal_Subtitle',
    nextShipmentOn: 'PauseModal_NextShipmentOn',
    resumeOn: 'PauseModal_ResumeOn',
    cancel: 'PauseModal_Cancel',
    confirm: 'PauseModal_ConfirmPause',
    confirmationTitle: 'PauseModal_ConfirmationTitle',
    confirmationQuestion: 'PauseModal_ConfirmationQuestion',
    submitting: 'PauseModal_Pausing',
    yesSure: 'PauseModal_YesSure',
    goBack: 'PauseModal_GoBack',
  },
  deliveryDate: {
    prefix: 'DeliveryDateModal',
    title: 'DeliveryDateModal_Title',
    subtitle: 'DeliveryDateModal_Subtitle',
    nextShipmentOn: 'DeliveryDateModal_NextShipmentOn',
    resumeOn: 'DeliveryDateModal_ResumeOn',
    cancel: 'DeliveryDateModal_Cancel',
    confirm: 'DeliveryDateModal_Confirm',
    confirmationTitle: 'DeliveryDateModal_ConfirmationTitle',
    confirmationQuestion: 'DeliveryDateModal_ConfirmationQuestion',
    submitting: 'DeliveryDateModal_Updating',
    yesSure: 'DeliveryDateModal_YesSure',
    goBack: 'DeliveryDateModal_GoBack',
  },
};

function PauseSubscriptionModal({
  open,
  onClose,
  onConfirm,
  isSubmitting,
  minDate = '',
  optionDates = {},
  mode = 'pause',
}) {
  const { t } = useTranslation();
  const copy = MODE_COPY[mode] || MODE_COPY.pause;
  const [selected, setSelected] = useState('skip_1');
  const [date, setDate] = useState('');
  const [showConfirmation, setShowConfirmation] = useState(false);
  const isDateValid = !minDate || !date || date >= minDate;

  useEffect(() => {
    if (open) {
      setSelected('skip_1');
      setDate('');
      setShowConfirmation(false);
    }
  }, [open]);

  const handleProceed = () => {
    if (selected === 'specific_date' && (!date || !isDateValid)) return;
    setShowConfirmation(true);
  };

  const handleFinalConfirm = () => {
    onConfirm({ option: selected, date: selected === 'specific_date' ? date : null });
  };

  const handleClose = () => {
    setShowConfirmation(false);
    onClose();
  };

  return (
    <Modal open={open} handleClose={handleClose} className="!t-max-w-[90vw]">
      <div className="t-w-full t-max-w-lg t-p-2 lg:t-p-4 t-flex t-flex-col t-gap-4">
        {!showConfirmation ? (
          <>
            <h2 className="t-text-xl t-font-bold t-m-0">{t(copy.title)}</h2>
            <p className="t-text-sm t-text-gray-600 t-m-0">{t(copy.subtitle)}</p>

            <div className="t-flex t-flex-col t-gap-3">
              {PAUSE_OPTIONS.map((opt) => (
                <label
                  key={opt.id}
                  className="t-flex t-items-start t-gap-3 t-rounded-lg t-border t-border-gray-200 t-px-3 t-py-2 t-cursor-pointer"
                >
                  <input
                    type="radio"
                    name="pause-option"
                    className="t-mt-1"
                    value={opt.id}
                    checked={selected === opt.id}
                    onChange={() => setSelected(opt.id)}
                  />
                  <div className="t-flex t-flex-col">
                    <span className="t-text-sm t-font-medium t-text-gray-900">
                      {t(`${copy.prefix}_${opt.labelSuffix}`, { defaultValue: opt.label })}
                    </span>
                    {opt.id === 'specific_date' ? (
                      <span className="t-text-xs t-text-gray-600">
                        {selected === 'specific_date' && date
                          ? t(copy.resumeOn, {
                              date: new Date(date + 'T00:00:00').toLocaleDateString('en-GB', {
                                day: 'numeric',
                                month: 'long',
                                year: 'numeric',
                              }),
                            })
                          : t(`${copy.prefix}_${opt.descSuffix}`, { defaultValue: opt.description })}
                      </span>
                    ) : null}
                    {(opt.id === 'skip_1' || opt.id === 'skip_2') && optionDates?.[opt.id] && (
                      <span className="t-mt-1 t-text-xs t-text-gray-600">
                        {t(copy.nextShipmentOn, { date: optionDates[opt.id] })}
                      </span>
                    )}
                    {opt.id === 'specific_date' && selected === 'specific_date' && (
                      <input
                        type="date"
                        className="t-mt-2 t-border t-border-gray-300 t-rounded-md t-px-2 t-py-1 t-text-sm"
                        value={date}
                        min={minDate || undefined}
                        onChange={(e) => setDate(e.target.value)}
                      />
                    )}
                  </div>
                </label>
              ))}
            </div>

            <div className="t-flex t-flex-col-reverse sm:t-flex-row t-justify-between t-items-stretch sm:t-items-center t-gap-2 t-mt-2">
              <button
                type="button"
                className="t-text-sm t-text-gray-700 hover:t-underline t-text-center sm:t-text-left"
                onClick={handleClose}
              >
                {t(copy.cancel)}
              </button>
              <button
                type="button"
                className="t-inline-flex t-items-center t-justify-center t-rounded-full t-px-4 t-py-2 t-text-sm t-font-semibold t-bg-secondary t-text-white hover:t-bg-teal-800 disabled:t-opacity-60 t-w-full sm:t-w-auto"
                onClick={handleProceed}
                disabled={selected === 'specific_date' && (!date || !isDateValid)}
              >
                {t(copy.confirm)}
              </button>
            </div>
          </>
        ) : (
          <>
            <h2 className="t-text-xl t-font-bold t-m-0 t-text-center">{t(copy.confirmationTitle)}</h2>
            <p className="t-text-base t-font-semibold t-text-center t-m-0">
              {t(copy.confirmationQuestion)}
            </p>

            <div className="t-flex t-flex-col sm:t-flex-row t-items-stretch sm:t-items-center t-gap-3 t-mt-2">
              <button
                type="button"
                className="t-flex-1 t-inline-flex t-items-center t-justify-center t-rounded-lg t-px-4 t-py-2 t-text-sm t-font-semibold t-bg-secondary t-text-white hover:t-bg-teal-800 disabled:t-opacity-60"
                onClick={handleFinalConfirm}
                disabled={isSubmitting}
              >
                {isSubmitting ? t(copy.submitting) : t(copy.yesSure)}
              </button>
              <button
                type="button"
                className="t-flex-1 t-inline-flex t-items-center t-justify-center t-rounded-lg t-px-4 t-py-2 t-text-sm t-font-semibold t-bg-red-400 t-text-white hover:t-bg-red-500"
                onClick={() => setShowConfirmation(false)}
                disabled={isSubmitting}
              >
                {t(copy.goBack)}
              </button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}

export default PauseSubscriptionModal;
